import { GET_STORES_MAX_DISTANCE_KM, Locale } from '@headless-workspace/config';
import { Repository, Result, ResultFailureFromApiError, ResultSuccess } from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { mapStoreInfo, StoreInfoDTO, StoreInfoListDTO, StoreInfoValue } from '../../../../server';

const DEFAULT_CAPABILITIES = 'all';

export interface StoreRepositorySpec {
    getStoreById(locale: Locale, storeId: string, productIds?: string[]): Promise<Result<StoreInfoValue>>;

    getStoresByIdsAndLocation(
        locale: Locale,
        productIds: string[],
        latitude: number,
        longitude: number,
    ): Promise<Result<StoreInfoValue[]>>;
}

const PRODUCT_IDS_SEPARATOR = ';';

export class StoreRepository extends Repository implements StoreRepositorySpec {
    path = '/store/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    async getStoreById(locale: Locale, storeId: string, productIds: string[] = []): Promise<Result<StoreInfoValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/stores/${storeId}`, {
                locale,
                productIds: productIds.join(PRODUCT_IDS_SEPARATOR) || undefined,
            });
            const result = StoreInfoDTO.parse(response);

            return ResultSuccess(mapStoreInfo(result));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getStoresByIdsAndLocation(
        locale: Locale,
        productIds: string[],
        latitude: number,
        longitude: number,
    ): Promise<Result<StoreInfoValue[]>> {
        try {
            const productIdsMapped = productIds.join(PRODUCT_IDS_SEPARATOR);

            const response = await this.datasource.getResource(`${this.path}/stores`, {
                locale,
                productIds: productIdsMapped,
                latitude,
                longitude,
                maxDistance: GET_STORES_MAX_DISTANCE_KM,
                capabilities: DEFAULT_CAPABILITIES,
            });
            const results = StoreInfoListDTO.parse(response);

            return ResultSuccess(results.stores.map(mapStoreInfo));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError);
        }
    }
}
